import { useEffect, useState } from "react";
import { Clock, Loader2, Save, Info } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

const TimeField = ({ label, hint, value, onChange, testid }) => (
  <label className="block">
    <span className="block text-[13px] font-medium text-[#1D1D1F]">{label}</span>
    {hint ? <span className="mb-1.5 block text-[12px] text-[#8E8E93]">{hint}</span> : null}
    <input
      data-testid={testid}
      type="time"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 h-11 w-full min-w-0 max-w-full box-border appearance-none rounded-xl border border-[#E1E1E6] bg-white px-3 text-[15px] focus:border-[#0071E3] focus:outline-none"
    />
  </label>
);

export default function AttendanceRules() {
  const { t } = useT();
  const { readOnly } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/attendance/fingerprint/settings").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/attendance/fingerprint/settings", {
        rule_arrival_full: s.rule_arrival_full,
        rule_depart_full: s.rule_depart_full,
        rule_midday_start: s.rule_midday_start,
        rule_midday_end: s.rule_midday_end,
        eod_threshold: s.eod_threshold,
        rule_effective_from: s.rule_effective_from || "",
        excluded_pins: (s.excluded_pins || []),
      });
      toast.success(t("set.saved"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (!s) {
    return <div className="grid place-items-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[#B0B0B8]" /></div>;
  }

  return (
    <div data-testid="attendance-rules-page" className="w-full space-y-6">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#EAF2FE] text-[#0071E3]"><Clock className="h-5 w-5" /></span>
        <div>
          <h1 className="text-[24px] font-semibold tracking-tight">{t("set.attendanceRules")}</h1>
          <p className="text-[13px] text-[#8E8E93]">{t("set.attendanceRulesDesc")}</p>
        </div>
      </div>

      <div className="flex items-start gap-2.5 rounded-2xl border border-[#FDE8C4] bg-[#FFF8EC] p-4">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#B4740A]" />
        <p className="text-[13px] leading-relaxed text-[#8A5A00]">{t("set.ruleNote")}</p>
      </div>

      <div className="liquid-glass-card space-y-5 p-5 sm:p-6">
        <h2 className="text-[15px] font-semibold">{t("set.fullDayTitle")}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TimeField testid="rule-arrival-full" label={t("set.arrivalFull")} hint={t("set.arrivalFullHint")} value={s.rule_arrival_full} onChange={(v) => set("rule_arrival_full", v)} />
          <TimeField testid="rule-depart-full" label={t("set.departFull")} hint={t("set.departFullHint")} value={s.rule_depart_full} onChange={(v) => set("rule_depart_full", v)} />
        </div>

        <div className="h-px bg-black/[0.06]" />
        <h2 className="text-[15px] font-semibold">{t("set.halfDayTitle")}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TimeField testid="rule-midday-start" label={t("set.middayStart")} hint={t("set.middayHint")} value={s.rule_midday_start} onChange={(v) => set("rule_midday_start", v)} />
          <TimeField testid="rule-midday-end" label={t("set.middayEnd")} hint={t("set.middayHint")} value={s.rule_midday_end} onChange={(v) => set("rule_midday_end", v)} />
        </div>

        <div className="h-px bg-black/[0.06]" />
        <h2 className="text-[15px] font-semibold">{t("set.otherTitle")}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TimeField testid="rule-eod" label={t("set.eod")} hint={t("set.eodHint")} value={s.eod_threshold} onChange={(v) => set("eod_threshold", v)} />
          <label className="block">
            <span className="block text-[13px] font-medium text-[#1D1D1F]">{t("set.effectiveFrom")}</span>
            <span className="mb-1.5 block text-[12px] text-[#8E8E93]">{t("set.effectiveFromHint")}</span>
            <input
              data-testid="rule-effective-from"
              type="date"
              value={s.rule_effective_from || ""}
              onChange={(e) => set("rule_effective_from", e.target.value)}
              className="mt-1 h-11 w-full min-w-0 max-w-full box-border appearance-none rounded-xl border border-[#E1E1E6] bg-white px-3 text-[15px] focus:border-[#0071E3] focus:outline-none"
            />
          </label>
        </div>
      </div>

      <div className="flex justify-end">
        {!readOnly && (
        <button data-testid="rules-save" onClick={save} disabled={saving}
          className="flex h-11 items-center gap-2 rounded-full bg-[#0071E3] px-6 text-[15px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98] disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("set.save")}
        </button>
        )}
      </div>
    </div>
  );
}
